import {
  booleanAttribute,
  Component,
  DoCheck,
  input,
  model,
  numberAttribute,
  OnInit,
} from '@angular/core';

import { generateId } from '@lc/util';
import { AbstractControl, ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import { BspkDropdown, DropdownOption, DropdownSize } from '../dropdown';
import { BspkFormField } from '../form-field';
import { noop, provideValidator, provideValueAccessor } from '../util';
import { ControlValidator } from '../util/control-validator';

@Component({
  selector: 'bspk-dropdown-field',
  imports: [BspkDropdown, BspkFormField],
  templateUrl: './dropdown-field.component.html',
  styleUrl: './dropdown-field.component.scss',
  providers: [provideValueAccessor(BspkDropdownField), provideValidator(BspkDropdownField)],
})
export class BspkDropdownField<TValue = string>
  extends ControlValidator
  implements ControlValueAccessor, Validator, DoCheck, OnInit {
  readonly value = model<TValue | null>();
  readonly options = input.required<DropdownOption<TValue>[]>();
  readonly label = input.required<string>();
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly errorMessage = input<string>('');
  readonly helperText = input<string>('');
  /**
   * Determines how many items to display at a time
   * minimum: 3
   * maximum: 10
   * @default 5
   * @see BspkMenu.itemCount
   */
  readonly itemCount = input(5, { transform: numberAttribute });
  readonly placeholder = input<string>('Select...');
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly size = input<DropdownSize>('medium');
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly controlId = input<string>(generateId('dropdown'));

  private _onChange: (value: TValue | null) => void = noop;
  private _onTouched: VoidFunction = noop;

  handleChange(event: TValue[]): void {
    const value = event[0] ?? null;
    this.value.set(value);
    this._onChange(value);
  }

  handleBlur() {
    this._onTouched();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // noop
  }

  writeValue(obj: any): void {
    this.value.set(obj);
  }

  registerOnValidatorChange(_fn: VoidFunction): void {
    // noop
  }

  validate(control: AbstractControl<TValue[]>): ValidationErrors | null {
    let errors: ValidationErrors | null = null;

    if (this.required() && !this.value()) {
      errors ??= {};
      errors['required'] = true;
    }

    return errors;
  }
}
